import * as React from 'react';
import { EditSection } from '../../components/Layout';
import i18n from '@/utils/i18n';
import { FaLock, FaSync } from 'react-icons/fa';
import { InvoiceRow } from './InvoiceRow';
import { useSyncBillomatInvoicesForStoreMutation } from '../../graphql/mutations/syncBillomatInvoicesForStore.generated';
import { Button, Input, Label } from 'reactstrap';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toCsvBlob } from '@/utils/data';
import {
  download,
  downloadJsonDataAsExcelFile,
  sanitizeFilename,
} from '@/utils/download';
import { formatBillomatPrice } from '@/utils/formatter';
import ExportButton from '@/components/Button/ExportButton';

// Both explicit shapes are matched before `new Date`, and both are built from their
// own parts: `new Date` accepts "03.04.2026" and reads it MONTH-first, and it parses
// "2026-04-03" as UTC midnight, which local getters then read as the day before west
// of UTC. The dates here carry no time, so they are local midnight throughout.
function parseInvoiceDate(dateStr?: string | null): Date | null {
  if (!dateStr) return null;
  const german = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (german) {
    return new Date(
      parseInt(german[3], 10),
      parseInt(german[2], 10) - 1,
      parseInt(german[1], 10),
    );
  }
  const iso = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(
      parseInt(iso[1], 10),
      parseInt(iso[2], 10) - 1,
      parseInt(iso[3], 10),
    );
  }
  const parsed = new Date(dateStr);
  if (isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

type InvoiceListProps = {
  invoices: any[];
  loading: boolean;
  allowedToAccess: boolean;
  onSave?: () => void;
  setInvoices: React.Dispatch<React.SetStateAction<any[]>>;
  invoiceTag: string;
  currencySymbol?: string | null;
  isFinanceAndNotAdmin?: boolean;
  onRefresh?: () => void;
  storeId?: string | null;
  storeIntegrationToken?: string | null;
};

export function InvoiceList({
  invoices,
  loading,
  allowedToAccess,
  onSave,
  setInvoices,
  invoiceTag,
  currencySymbol,
  isFinanceAndNotAdmin,
  onRefresh,
  storeId,
  storeIntegrationToken,
}: InvoiceListProps) {
  const [syncInvoices, { loading: syncing }] =
    useSyncBillomatInvoicesForStoreMutation();

  const handleSyncInvoices = async () => {
    if (!storeId) return;
    try {
      const result = await syncInvoices({
        variables: { store_id: storeId },
      });
      const syncResult = result.data?.syncBillomatInvoicesForStore;
      if (syncResult) {
        if (syncResult.errors > 0) {
          alert(
            i18n.t('Invoice:syncError', { errors: syncResult.errors }) +
              '\n' +
              i18n.t('Invoice:syncSuccess', {
                synced: syncResult.synced,
                total: syncResult.total,
              }),
          );
        } else {
          alert(
            i18n.t('Invoice:syncSuccess', {
              synced: syncResult.synced,
              total: syncResult.total,
            }),
          );
        }
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error: any) {
      alert('Error syncing invoices: ' + error.message);
    }
  };

  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');

  const filteredInvoices = React.useMemo(() => {
    if (!dateFrom && !dateTo) {
      return invoices;
    }

    const fromDate = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const toDate = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

    return invoices.filter((invoice) => {
      const invoiceDate = parseInvoiceDate(invoice.date);
      if (!invoiceDate) {
        return false;
      }
      if (fromDate && invoiceDate < fromDate) {
        return false;
      }
      if (toDate && invoiceDate > toDate) {
        return false;
      }
      return true;
    });
  }, [invoices, dateFrom, dateTo]);

  const getExportRows = () => {
    const headerInvoiceNumber = i18n.t('Invoice:invoiceNumber');
    const headerInvoiceDate = i18n.t('Invoice:invoiceDate');
    const headerAmount = i18n.t('Invoice:amount');
    const headerStatus = i18n.t('Invoice:status');

    return filteredInvoices.map((invoice) => ({
      [headerInvoiceNumber]: invoice.invoice_number,
      [headerInvoiceDate]: invoice.date,
      [headerAmount]: formatBillomatPrice(
        invoice.parsed_amount,
        currencySymbol || '€',
      ),
      [headerStatus]: i18n.t('Invoice:invoiceStatus:' + invoice.status),
    }));
  };

  const exportFilenameBase = React.useMemo(() => {
    const fromLabel = dateFrom || 'all';
    const toLabel = dateTo || 'all';
    return sanitizeFilename(
      `invoices_${invoiceTag}_${fromLabel}_to_${toLabel}`,
    );
  }, [dateFrom, dateTo, invoiceTag]);

  const handleExportCsv = () => {
    if (filteredInvoices.length === 0) {
      return;
    }
    const blob = toCsvBlob(getExportRows());
    download(blob, `${exportFilenameBase}.csv`);
  };

  const handleExportExcel = () => {
    if (filteredInvoices.length === 0) {
      return;
    }
    downloadJsonDataAsExcelFile(getExportRows(), exportFilenameBase);
  };

  let props = {};
  if (allowedToAccess && onSave != null) {
    props = {
      onSave: onSave,
    };
  }

  const billomatLink =
    `https://pickpack2019.billomat.net/app/invoices?search%5Bnumber%5D=&search%5Blabel%5D=&search%5B` +
    `date_start%5D=&search%5Bdate_end%5D=&search%5Bpayment_date_start%5D=&search%5Bpayment_date_end%5D=&search%5B` +
    `name_number%5D=&search%5Bname_number_article%5D=&search%5Btype%5D=ALL&search%5Bdig-status%5D=ALL` +
    `&search%5Btag%5D%5B%5D=${encodeURIComponent(invoiceTag || '')}#bulkForm`;

  function toggleHidden(id: string) {
    setInvoices((oldInvoices: any[]) => {
      const newInvoices = [...oldInvoices];
      const index = newInvoices.findIndex((i) => i.id === id);
      if (index !== -1) {
        newInvoices[index] = {
          ...newInvoices[index],
          hidden: !newInvoices[index].hidden,
        };
      }
      return newInvoices;
    });
  }

  return (
    <EditSection title={i18n.t('Invoice:invoices')} {...props}>
      {allowedToAccess && (
        <a href={billomatLink} target="_blank" rel="noopener noreferrer">
          <FaLock /> View in billomat
        </a>
      )}
      {isFinanceAndNotAdmin && storeId && (
        <Button
          size="sm"
          color="secondary"
          className="ms-2"
          onClick={handleSyncInvoices}
          disabled={syncing}
        >
          <FaLock style={{ marginRight: '5px' }} />
          <FaSync
            className={syncing ? 'fa-spin' : ''}
            style={{ marginRight: '5px' }}
          />
          {syncing
            ? i18n.t('Invoice:syncingInvoices')
            : i18n.t('Invoice:syncAllInvoices')}
        </Button>
      )}
      <div
        className="d-flex align-items-end mt-3 mb-3 flex-wrap gap-3"
        style={{ gap: '1rem', float: 'right' }}
      >
        <div>
          <Label for="invoice-date-from" className="mb-1">
            {i18n.t('Invoice:dateFrom')}
          </Label>
          <Input
            id="invoice-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <Label for="invoice-date-to" className="mb-1">
            {i18n.t('Invoice:dateTo')}
          </Label>
          <Input
            id="invoice-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <ExportButton
          disabled={loading || filteredInvoices.length === 0}
          csvCallback={handleExportCsv}
          excelCallback={handleExportExcel}
        />
      </div>
      <div className="mt-2 mb-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead style={{ maxWidth: '125px', wordWrap: 'break-word' }}>
                {i18n.t('Invoice:invoiceNumber')}
              </TableHead>
              <TableHead style={{ maxWidth: '125px', wordWrap: 'break-word' }}>
                {i18n.t('Invoice:invoiceDate')}
              </TableHead>
              <TableHead>{i18n.t('Invoice:title')}</TableHead>
              <TableHead style={{ maxWidth: '125px', wordWrap: 'break-word' }}>
                {i18n.t('Invoice:status')}
              </TableHead>
              {allowedToAccess && (
                <TableHead
                  style={{ maxWidth: '125px', wordWrap: 'break-word' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FaLock />
                    {i18n.t('Invoice:invoiceTotal')}
                  </div>
                </TableHead>
              )}
              <TableHead>{i18n.t('Invoice:amount')}</TableHead>
              <TableHead>{i18n.t('Invoice:paymentDirectionTitle')}</TableHead>
              <TableHead>{i18n.t('Invoice:download')}</TableHead>
              <TableHead>{i18n.t('Invoice:billedContainers')}</TableHead>
              {allowedToAccess && (
                <TableHead>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FaLock style={{ marginRight: '5px' }} />
                    {i18n.t('Invoice:visible')}
                  </div>
                </TableHead>
              )}
              {allowedToAccess && (
                <TableHead>
                  <FaLock />
                </TableHead>
              )}
              {isFinanceAndNotAdmin && (
                <TableHead>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FaLock style={{ marginRight: '5px' }} />
                    {i18n.t('Invoice:actions')}
                  </div>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>

          <TableBody>
            {filteredInvoices.map((invoice: any) => {
              return (
                <InvoiceRow
                  key={invoice.id}
                  invoice={invoice}
                  isAdmin={allowedToAccess}
                  toggleHidden={toggleHidden}
                  currencySymbol={currencySymbol || '€'}
                  isFinanceAndNotAdmin={isFinanceAndNotAdmin}
                  onRefresh={onRefresh}
                  storeIntegrationToken={storeIntegrationToken}
                />
              );
            })}

            {loading && (
              <TableRow>
                <TableCell colSpan={11}>loading...</TableCell>
              </TableRow>
            )}
            {!loading && filteredInvoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={11}>
                  {i18n.t('Invoice:noInvoices')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </EditSection>
  );
}
